'use client'

import {useEffect, useMemo, useState} from 'react'
import {Bot, Edit, Sparkles, Trash2} from 'lucide-react'
import type {Contact, ContactLabel, KeywordRule, ReplyPayload, SettingsState} from '@/types'
import {emptyReplyPayload, payloadText} from '@/lib/utils'
import {resolveTargets} from '@/lib/targetResolution'
import {PageHeader} from '@/components/PageHeader'
import {Switch} from '@/components/Switch'
import {message} from '@/components/Toast'
import {ContactSelectorPanel} from '@/components/ContactSelectorPanel'
import {ReplyPayloadEditor} from '@/components/ReplyPayloadEditor'

/**
 * Auto-reply settings page. Layout mirrors the `AIReplyView` from
 * WechatBotConsole.jsx: left contact selector, right side two full-width
 * stacked cards (AI prompt · keyword rules).
 */

type RuleDraft = {
    id?: number
    keyword: string
    matchType: KeywordRule['matchType']
    replyPayload: ReplyPayload
    enabled: boolean
}

const createDraft = (rule?: KeywordRule): RuleDraft => ({
    id: rule?.id,
    keyword: rule?.keyword || '',
    matchType: rule?.matchType || 'exact',
    replyPayload: rule?.replyPayload || emptyReplyPayload(rule?.replyType || 'text'),
    enabled: rule?.enabled ?? true,
})

const matchTypeLabel = (value: string) => {
    if (value === 'exact') return '精准匹配'
    if (value === 'fuzzy') return '模糊匹配'
    if (value === 'regex') return '正则匹配'
    return value
}

export function AutoReplyView({
    contacts,
    labels,
    settings,
    rules,
    saveSettings,
    saveRule,
    deleteRule,
}: {
    contacts: Contact[]
    labels: ContactLabel[]
    settings: SettingsState
    rules: KeywordRule[]
    saveSettings: (patch: Partial<SettingsState>) => void
    saveRule: (rule: Partial<KeywordRule>) => Promise<void> | void
    deleteRule: (id: number) => Promise<void> | void
}) {
    const aiReply = settings.aiReply
    const [promptDraft, setPromptDraft] = useState(aiReply.prompt)
    const [ruleDraft, setRuleDraft] = useState<RuleDraft>(createDraft())
    const [ruleError, setRuleError] = useState('')

    useEffect(() => {
        setPromptDraft(aiReply.prompt)
    }, [aiReply.prompt])

    const addContact = (wxid: string) => {
        if (aiReply.contactWxids.includes(wxid)) return
        saveSettings({
            aiReply: {
                ...aiReply,
                contactWxids: [...aiReply.contactWxids, wxid],
            },
        })
    }

    const removeContact = (wxid: string) => {
        saveSettings({
            aiReply: {
                ...aiReply,
                contactWxids: aiReply.contactWxids.filter((item) => item !== wxid),
            },
        })
    }

    const contactLabelIds = aiReply.contactLabelIds ?? []

    const addLabel = (id: number) => {
        if (contactLabelIds.includes(id)) return
        saveSettings({
            aiReply: {
                ...aiReply,
                contactLabelIds: [...contactLabelIds, id],
            },
        })
    }

    const removeLabel = (id: number) => {
        saveSettings({
            aiReply: {
                ...aiReply,
                contactLabelIds: contactLabelIds.filter((x) => x !== id),
            },
        })
    }

    const clearTargets = () => {
        saveSettings({
            aiReply: {...aiReply, contactWxids: [], contactLabelIds: []},
        })
    }



    const resolvedCount = useMemo(
        () => resolveTargets({contactWxids: aiReply.contactWxids, labelIds: contactLabelIds, contacts}).length,
        [aiReply.contactWxids, contactLabelIds, contacts],
    )

    const toggleAi = () => {
        if (!aiReply.aiEnabled && !settings.aiConfig?.apiKey) {
            message.warning('请先在「系统设置 → AI 大模型配置」中配置 API Key')
            return
        }
        saveSettings({
            aiReply: {...aiReply, aiEnabled: !aiReply.aiEnabled},
        })
    }

    const toggleKeyword = () => {
        saveSettings({
            aiReply: {...aiReply, keywordEnabled: !aiReply.keywordEnabled},
        })
    }

    const savePrompt = () => {
        saveSettings({
            aiReply: {...aiReply, prompt: promptDraft},
        })
    }

    const isEditingRule = ruleDraft.id !== undefined

    const resetRuleDraft = () => {
        setRuleDraft(createDraft())
        setRuleError('')
    }

    const handleSaveRule = () => {
        const keyword = ruleDraft.keyword.trim()
        const replyText = payloadText(ruleDraft.replyPayload).trim()
        if (!keyword) {
            setRuleError('请输入触发关键词')
            return
        }
        if (!replyText) {
            setRuleError('请输入回复内容')
            return
        }
        void saveRule({
            id: ruleDraft.id,
            keyword,
            reply: replyText,
            replyType: ruleDraft.replyPayload.type || 'text',
            replyPayload: ruleDraft.replyPayload,
            matchType: ruleDraft.matchType,
            enabled: ruleDraft.enabled,
        })
        resetRuleDraft()
    }

    const handleEditRule = (rule: KeywordRule) => {
        setRuleDraft({
            id: rule.id,
            keyword: rule.keyword,
            matchType: rule.matchType,
            replyPayload: rule.replyPayload,
            enabled: rule.enabled,
        })
        setRuleError('')
    }

    const handleToggleRule = (rule: KeywordRule) => {
        void saveRule({...rule, enabled: !rule.enabled})
    }

    const handleRemoveRule = (rule: KeywordRule) => {
        void deleteRule(rule.id)
        if (ruleDraft.id === rule.id) {
            resetRuleDraft()
        }
    }

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader
                title="自动回复"
                description="为选定好友配置自动回复规则。关键词回复和 AI 回复各自独立开关，命中关键词时优先回复关键词。"
                icon={<Sparkles size={20}/>}
            />
            <div className="flex-1 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden p-3 md:p-6 gap-3 md:gap-6">
                <ContactSelectorPanel
                    contacts={contacts}
                    selectedWxids={aiReply.contactWxids}
                    onSelect={addContact}
                    onRemove={removeContact}
                    onClear={clearTargets}
                    labels={labels}
                    selectedLabelIds={contactLabelIds}
                    onLabelSelect={addLabel}
                    onLabelRemove={removeLabel}
                />
                <div className="flex-1 overflow-y-auto pr-2">
                    <div className="max-w-5xl space-y-6 pb-6">
                        <AiReplyCard
                            enabled={aiReply.aiEnabled}
                            contactCount={resolvedCount}
                            prompt={promptDraft}
                            onPromptChange={setPromptDraft}
                            onToggle={toggleAi}
                            onSave={savePrompt}
                        />
                        <KeywordRuleCard
                            enabled={aiReply.keywordEnabled}
                            isEditing={isEditingRule}
                            ruleDraft={ruleDraft}
                            setRuleDraft={setRuleDraft}
                            resetRuleDraft={resetRuleDraft}
                            ruleError={ruleError}
                            onToggle={toggleKeyword}
                            onSave={handleSaveRule}
                            rules={rules}
                            onEditRule={handleEditRule}
                            onToggleRule={handleToggleRule}
                            onRemoveRule={handleRemoveRule}
                            voiceId={settings.tts?.selectedVoiceId}
                        />
                    </div>
                </div>
            </div>
        </section>
    )
}

const PERSONA_TEMPLATES = [
    {
        id: 'custom',
        label: '🎨 自定义人设',
        prompt: '',
    },
    {
        id: 'assistant',
        label: '🤖 私人助理',
        prompt: '你现在是我的私人助理，请用自然、简洁、礼貌的方式代我回复消息。回复要专业但不失亲切，帮我处理日常沟通、安排事务、回答问题。如果对方问的事情你不确定，请礼貌地告知需要确认后回复。',
    },
    {
        id: 'cold-ceo',
        label: '🏢 高冷霸总',
        prompt: '你现在扮演一个高冷霸道总裁的角色来回复消息。说话简短有力，带点傲娇但内心关心对方。偶尔用"嗯"、"知道了"、"随你"这类简短回复，但关键时刻会展现温柔的一面。不要用表情符号，语气要冷淡但不失礼貌。',
    },
    {
        id: 'flirt',
        label: '🎣 海王/海后拉扯',
        prompt: '你现在扮演一个情商极高、擅长暧昧拉扯的角色来回复消息。回复要若即若离，让对方猜不透你的心思。善用反问、调侃、欲擒故纵的技巧。偶尔主动关心但不过分热情，保持神秘感和吸引力。语气轻松幽默，带点撩人但不油腻。',
    },
]

function AiReplyCard({
    enabled,
    contactCount,
    prompt,
    onPromptChange,
    onToggle,
    onSave,
}: {
    enabled: boolean
    contactCount: number
    prompt: string
    onPromptChange: (value: string) => void
    onToggle: () => void
    onSave: () => void
}) {
    const currentPersona = PERSONA_TEMPLATES.find(t => t.id !== 'custom' && t.prompt === prompt)?.id || 'custom'

    const handlePersonaChange = (personaId: string) => {
        const template = PERSONA_TEMPLATES.find(t => t.id === personaId)
        if (!template) return
        if (template.id === 'custom') {
            onPromptChange('')
        } else {
            onPromptChange(template.prompt)
        }
    }

    return (
        <div className="bg-white rounded-lg shadow-card border border-hair p-6 flex flex-col min-h-[300px]">
            <div className="flex justify-between items-center mb-4 pb-4 border-b border-hair">
                <div className="flex items-center gap-2">
                    <Sparkles size={18} className="text-accent"/>
                    <h3 className="font-medium text-ink">AI 智能回复</h3>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-sm text-muted">{enabled ? '已开启' : '已关闭'}</span>
                    <Switch checked={enabled} onChange={onToggle}/>
                </div>
            </div>
            <div className={`flex-1 flex flex-col ${enabled ? '' : 'opacity-50 pointer-events-none'}`}>
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <label className="text-sm text-soft shrink-0">人设模板：</label>
                        <select
                            value={currentPersona}
                            onChange={(e) => handlePersonaChange(e.target.value)}
                            className="w-44 border border-hair rounded-md px-2.5 py-1.5 text-sm text-ink bg-white focus:outline-none focus:border-accent cursor-pointer"
                        >
                            {PERSONA_TEMPLATES.map(t => (
                                <option key={t.id} value={t.id}>{t.label}</option>
                            ))}
                        </select>
                    </div>
                    <span className="pill">对左侧 {contactCount} 个好友生效</span>
                </div>
                <label className="text-sm text-soft mb-1.5">系统提示词 (System Prompt)：</label>
                <textarea
                    className="flex-1 w-full border border-hair rounded-md p-4 bg-page focus:outline-none focus:bg-white focus:border-accent resize-none text-sm text-soft leading-relaxed min-h-[180px]"
                    value={prompt}
                    onChange={(event) => onPromptChange(event.target.value)}
                    placeholder="输入系统提示词，用于指导 AI 的回复风格与内容"
                />
                <div className="mt-4 flex justify-end">
                    <button
                        type="button"
                        onClick={onSave}
                        className="bg-accent hover:bg-accent-hover text-white px-8 py-2.5 rounded shadow-card transition-colors text-sm font-medium"
                    >
                        保存 Prompt
                    </button>
                </div>
            </div>
        </div>
    )
}

function KeywordRuleCard({
    enabled,
    isEditing,
    ruleDraft,
    setRuleDraft,
    resetRuleDraft,
    ruleError,
    onToggle,
    onSave,
    rules,
    onEditRule,
    onToggleRule,
    onRemoveRule,
    voiceId,
}: {
    enabled: boolean
    isEditing: boolean
    ruleDraft: RuleDraft
    setRuleDraft: (draft: RuleDraft) => void
    resetRuleDraft: () => void
    ruleError: string
    onToggle: () => void
    onSave: () => void
    rules: KeywordRule[]
    onEditRule: (rule: KeywordRule) => void
    onToggleRule: (rule: KeywordRule) => void
    onRemoveRule: (rule: KeywordRule) => void
    voiceId?: string
}) {
    return (
        <div className="bg-white rounded-lg shadow-card border border-hair p-6 flex flex-col min-h-[400px]">
            <div className="flex justify-between items-center mb-4 pb-4 border-b border-hair">
                <div className="flex items-center gap-2">
                    <Bot size={18} className="text-accent"/>
                    <h3 className="font-medium text-ink">关键词回复 (优先)</h3>
                </div>
                <div className="flex items-center gap-3">
                    {isEditing ? (
                        <button
                            type="button"
                            className="text-xs text-muted hover:text-ink"
                            onClick={resetRuleDraft}
                        >
                            取消编辑
                        </button>
                    ) : null}
                    <span className="text-sm text-muted">{enabled ? '已开启' : '已关闭'}</span>
                    <Switch checked={enabled} onChange={onToggle}/>
                </div>
            </div>
            <div className={`flex-1 flex flex-col overflow-hidden ${enabled ? '' : 'opacity-50 pointer-events-none'}`}>
                {/* Composer-style rule editor */}
                <div className="flex flex-col bg-page p-5 rounded-lg border border-hair shrink-0 mb-5 shadow-card">
                    <div className="flex gap-4 mb-4">
                        <select
                            value={ruleDraft.matchType}
                            onChange={(event) =>
                                setRuleDraft({
                                    ...ruleDraft,
                                    matchType: event.target.value as KeywordRule['matchType'],
                                })
                            }
                            className="w-32 h-10 border border-hair rounded-md px-3 text-sm text-ink focus:outline-none focus:border-accent bg-white cursor-pointer"
                        >
                            <option value="exact">精准匹配</option>
                            <option value="fuzzy">包含 (模糊)</option>
                            <option value="regex">正则匹配</option>
                        </select>
                        <input
                            type="text"
                            placeholder="触发关键词 (多个词可用 | 分隔，如：价格|报价)"
                            value={ruleDraft.keyword}
                            onChange={(event) => setRuleDraft({...ruleDraft, keyword: event.target.value})}
                            className="flex-1 h-10 border border-hair rounded-md px-3 text-sm text-ink placeholder:text-muted focus:outline-none focus:border-accent bg-white"
                        />
                    </div>
                    <div className="bg-white border border-hair rounded-md px-4 py-3 relative flex flex-col">
                        <ReplyPayloadEditor
                            payload={ruleDraft.replyPayload}
                            onChange={(replyPayload) => setRuleDraft({...ruleDraft, replyPayload})}
                            placeholder="回复文本内容... (支持与图片、文件、语音混合发送)"
                            voiceId={voiceId}
                            action={
                                <button
                                    type="button"
                                    onClick={onSave}
                                    className="bg-accent hover:bg-accent-hover text-white px-6 h-9 rounded-md text-sm transition-colors font-medium shadow-card"
                                >
                                    {isEditing ? '保存修改' : '添加规则'}
                                </button>
                            }
                        />
                    </div>
                    {ruleError ? (
                        <div className="mt-3 px-3 py-2 rounded bg-red-50 border border-red-100 text-xs text-red-600">
                            {ruleError}
                        </div>
                    ) : null}
                </div>

                {/* Rule list */}
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-muted">已添加关键词 ({rules.length})</div>
                </div>
                <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                    {rules.length === 0 ? (
                        <div className="text-center text-muted text-sm mt-10">暂无任何关键词规则</div>
                    ) : (
                        rules.map((rule) => {
                            const rowEditing = ruleDraft.id === rule.id
                            const replyText = rule.reply || payloadText(rule.replyPayload)
                            return (
                                <div
                                    key={rule.id}
                                    className={`flex justify-between items-start bg-white p-4 rounded-lg border transition-all group ${
                                        rowEditing
                                            ? 'border-accent bg-green-50/40 shadow-card'
                                            : 'border-hair hover:border-accent hover:shadow-card'
                                    }`}
                                >
                                    <div className="flex-1 overflow-hidden pr-4">
                                        <div className="flex items-center mb-2">
                                            <span className="text-sm font-medium text-ink mr-3">
                                                {rule.keyword}
                                            </span>
                                            <span className="text-[11px] bg-accent/10 text-accent px-2 py-0.5 rounded border border-accent/20">
                                                {matchTypeLabel(rule.matchType)}
                                            </span>
                                        </div>
                                        {replyText ? (
                                            <div className="text-[13px] text-soft break-words leading-relaxed">
                                                {replyText}
                                            </div>
                                        ) : null}
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <Switch
                                            checked={rule.enabled}
                                            onChange={() => onToggleRule(rule)}
                                        />
                                        <button
                                            type="button"
                                            title="编辑"
                                            className={`p-1.5 rounded hover:bg-page ${rowEditing ? 'text-accent' : 'text-muted hover:text-ink'}`}
                                            onClick={() => onEditRule(rule)}
                                        >
                                            <Edit size={16}/>
                                        </button>
                                        <button
                                            type="button"
                                            title="删除"
                                            className="p-1.5 rounded text-muted hover:bg-red-50 hover:text-red-500"
                                            onClick={() => onRemoveRule(rule)}
                                        >
                                            <Trash2 size={16}/>
                                        </button>
                                    </div>
                                </div>
                            )
                        })
                    )}
                </div>
            </div>
        </div>
    )
}
